import {
  array,
  boolean,
  type InferOutput,
  nullable,
  number,
  object,
  omit,
  string
} from 'valibot'

import { type Nullable } from '@/libs/utils'
import { nonNullableDateToStringSchema } from '@/libs/valibot'

import {
  RoundTypePicklist,
  ShipmentOrderAdditionalFormValueSchema
} from './shipment-order-additional-form-value'
import {
  shipmentOrderCardEntries,
  withExpenseTaxRequiredChecks,
  withIncomeTaxRequiredChecks,
  withTimeRestrictionChecks
} from './shipment-order-card-schema'

const scheduleWithShipmentOrderDispatchDetailFormValueBaseSchema = object({
  ...shipmentOrderCardEntries,
  /** 払い計上日（CONTRACTOR Partner/拠点のカスケード設定を初期値に適用、手動上書き可） */
  expenseRecordingDate: nonNullableDateToStringSchema,
  /** 払い運賃(円) */
  expense: nullable(number()),
  /**
   * 払い運賃(expense)の税区分マスタ ID（#3328 VOC_PJ-761、案件個別オーバーライド、矢羽根単位）。
   * パートナー選択時にパートナーの `defaultTaxCategoryId` を初期値に pre-fill。
   */
  expenseTaxCategoryId: nullable(number()),
  /**
   * 払い運賃(expense)の内税フラグ（VOC_PJ-844、true=内税 / null・false=外税、矢羽根単位）。
   * 拠点(IN_HOUSE)の `defaultIsTaxInclusive` を初期値に pre-fill。
   */
  expenseIsTaxInclusive: nullable(boolean()),
  /**
   * 払い運賃(expense)の端数処理（VOC_PJ-888 #3917、矢羽根単位）。切上/切捨/四捨五入。
   * `null` のときは BE 側でカスケード fallback。内税フラグと対称化。
   */
  expenseRoundType: nullable(RoundTypePicklist),
  // 開始時刻
  startAt: nonNullableDateToStringSchema,
  // 終了時刻
  endAt: nonNullableDateToStringSchema,
  /**
   * VOC_PJ-29,431 #1857 / #3541 指摘-8: 支払側付帯費用 (対 パートナー)。
   * 作成される DispatchDetail (矢羽根) 単位で 1 セット。払い運賃と同粒度のため root 直下に持つ。
   */
  additionalPayments: array(ShipmentOrderAdditionalFormValueSchema)
})

export const scheduleWithShipmentOrderDispatchDetailFormValueSchema =
  withExpenseTaxRequiredChecks(
    withIncomeTaxRequiredChecks(
      withTimeRestrictionChecks(
        scheduleWithShipmentOrderDispatchDetailFormValueBaseSchema
      )
    )
  )

/**
 * 手動追加の配送依頼データのスキーマ。
 * 日付6フィールド（shippingDate / deliveryDate / incomeRecordingDate / expenseRecordingDate / startAt / endAt）
 * をバリデーション不可の文字列として扱い、その他フィールドは shared 側定義を使用する。
 */
export const manualScheduleWithShipmentOrderDispatchDetailFormValueSchema =
  withExpenseTaxRequiredChecks(
    withIncomeTaxRequiredChecks(
      withTimeRestrictionChecks(
        object({
          ...omit(scheduleWithShipmentOrderDispatchDetailFormValueBaseSchema, [
            'shippingDate',
            'deliveryDate',
            'incomeRecordingDate',
            'expenseRecordingDate',
            'startAt',
            'endAt'
          ]).entries,
          ...object({
            shippingDate: string(),
            deliveryDate: string(),
            incomeRecordingDate: string(),
            expenseRecordingDate: string(),
            startAt: string(),
            endAt: string()
          }).entries
        })
      )
    )
  )

/**
 * 配送依頼（shipmentOrder）作成フォームの値
 */
export type ScheduleWithShipmentOrderDispatchDetailFormValue = Nullable<
  Omit<
    InferOutput<typeof scheduleWithShipmentOrderDispatchDetailFormValueSchema>,
    | 'note'
    | 'orderNo'
    | 'shippingDate'
    | 'deliveryDate'
    | 'incomeRecordingDate'
    | 'expenseRecordingDate'
    | 'startAt'
    | 'endAt'
  >
> & {
  note: string
  orderNo: string
  shippingDate: Date | string | null
  deliveryDate: Date | string | null
  incomeRecordingDate: Date | string | null
  expenseRecordingDate: Date | string | null
  startAt: Date | string | null
  endAt: Date | string | null
}
